import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import React, { useState, useEffect } from 'react'

/** Material UI **/
import { HighlightButton } from '../../styles/mui_custom_components'
import lockIcon from '../../assets/icons/lock.svg'
import userIcon from '../../assets/icons/user.svg'
import { logInStyles } from '../../styles/classes/LogInClasses'
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material'

import LogInContainer from '../../components/LogInContainer'
/** Validations **/
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

/** Images **/
import { envVariables, images } from '../../lib/Constants'

/** Redux **/
import { store } from '../../store'

/** Services **/
import { login } from '../../services/AuthService'
import { LoadingSplash } from '../../components/LoadingSplash'
import { getTimeSheetEntryFields } from '../../lib/Api'
import { authActions } from '../../store/signIn'
import { useDispatch } from 'react-redux'

const LogIn = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { classes } = logInStyles()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showErrors, setShowErrors] = useState(false)
  const [rememberMe, setRemember] = useState(true)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const authStore = store.getState().auth
    if (authStore.email) {
      setEmail(authStore.email)
      setRemember(true)
    }
  }, [])

  /** VALIDATIONS **/
  const validationSchema = yup.object().shape({
    email: yup.string().required(t('general.messages.errors.sign_in')),
    password: yup.string().required(t('general.messages.errors.sign_in'))
  })

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    mode: 'all',
    resolver: yupResolver(validationSchema)
  })

  const onSubmit = async () => {
    try {
      setLoading(true)
      await login(email, password, rememberMe)
      // here call the endpoint fot timeSheets details
      const response = await getTimeSheetEntryFields()
      const fields = response?.fields || []
      const foundTypeDetails = fields.find(
        (item) => item.name === 'Labor_Type__c'
      )
      dispatch(authActions.setTypeDetails(foundTypeDetails))
    } catch (error) {
      if (error.code >= 400 && error.code <= 599) {
        setShowErrors(true)
      }
      setLoading(false)
    }
  }

  const handleEmailChange = event => {
    setShowErrors(false)
    const value = event.target.value.replace(/\s/g, '')
    setEmail(value)
  }

  const handlePasswordChange = event => {
    setShowErrors(false)
    const value = event.target.value.replace(/\s/g, '')
    setPassword(value)
  }

  const handleChangeRemember = event => {
    setRemember(!rememberMe)
  }

  if (loading) return <LoadingSplash />
  return (
    <LogInContainer screen="sign_in">
      <img
        alt={'mywork Logo'}
        className={classes.myworkLogo}
        src={images.logo}
      />
      <Grid
        item
        data-testid={'sign_in_page'}
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Grid item className={classes.mainItem} mt={{ xs: 12, md: 10 }}>
          <Grid item justifyContent="center">
            <Typography className={classes.mainTitle}>
              {t('sign_in.management_portal')}
            </Typography>
          </Grid>
          <Grid item justifyContent="center">
            <Typography
              align={'center'}
              classes={{ root: classes.signMessage }}
            >
              {t('sign_in.main_message')}
            </Typography>
          </Grid>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <Box
              alignItems="center"
              alignContent="center"
              textAlign={'center'}
              justifyContent="center"
              mt={3}
            >
              <TextField
                classes={{ root: classes.fields }}
                variant="outlined"
                margin="normal"
                required
                id="email"
                placeholder={t('sign_in.email_address')}
                name="email"
                autoComplete="off"
                type="text"
                autoFocus
                error={!!errors.email}
                FormHelperTextProps={{
                  classes: {
                    root: classes.errorMessage,
                    error: classes.errorMessage
                  }
                }}
                {...register('email')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <img
                        src={userIcon}
                        className={classes.menuIcon}
                        alt="user icon"
                      />
                    </InputAdornment>
                  ),
                  classes: {
                    notchedOutline: classes.fieldsOutlined
                  }
                }}
                onInput={handleEmailChange}
                value={email}
              />
            </Box>
            <Box
              alignItems="center"
              alignContent="center"
              textAlign={'center'}
              justifyContent="center"
            >
              <TextField
                classes={{ root: classes.fields }}
                variant="outlined"
                margin="normal"
                required
                name="password"
                placeholder={t('sign_in.password')}
                type="password"
                id="password"
                autoComplete="new-password"
                error={!!errors.password}
                helperText={
                  errors.password && errors.email && errors.email.message
                }
                FormHelperTextProps={{
                  classes: {
                    root: classes.errorMessage,
                    error: classes.errorMessage
                  }
                }}
                {...register('password')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <img
                        src={lockIcon}
                        className={classes.menuIcon}
                        alt="lock icon"
                      />
                    </InputAdornment>
                  ),
                  classes: {
                    notchedOutline: classes.fieldsOutlined
                  }
                }}
                onInput={handlePasswordChange}
                value={password}
              />
            </Box>
            <Box hidden={!showErrors} width="560px" margin="0px auto">
              <Typography align={'left'} className={classes.errorMessage}>
                {t('sign_in.messages.wrong_user_password')}
              </Typography>
            </Box>

            <Grid container className={classes.rememberForgot}>
              <Grid item xs>
                <FormControlLabel
                  classes={{ label: classes.rememberMe }}
                  control={
                    <Checkbox
                      style={{ transform: 'scale(0.6)' }}
                      checked={rememberMe}
                      onChange={handleChangeRemember}
                      color="primary"
                    />
                  }
                  label={t('sign_in.remember_me')}
                />
              </Grid>
              <Grid item>
                <Box className={classes.linkBox}>
                  <Link
                    data-testid="forgot_password"
                    className={classes.link}
                    to="/forgot-password"
                    variant="body2"
                  >
                    {t('sign_in.forgot_password')}
                  </Link>
                </Box>
              </Grid>
            </Grid>

            <Box textAlign="center" pt={2}>
              <Typography
                component={'span'}
                align={'center'}
                className={classes.terms}
              >
                {t('sign_in.messages.terms_and_conditions')}{' '}
                <Link
                  to={{
                    pathname: envVariables.REACT_APP_FTC_TERMS_OF_SERVICE_URL
                  }}
                  target={'_blank'}
                  rel={'noopener noreferrer'}
                  className={classes.linkTerms}
                >
                  {t('sign_in.messages.terms_and_conditions_link')}
                </Link>
              </Typography>
            </Box>

            <Grid className={classes.buttons} container justifyContent="center">
              <Grid item xs textAlign={'end'}>
                <HighlightButton
                  id="sign_in_button"
                  data-testid="sign_in_button"
                  disabled={!email || !password}
                  type="submit"
                  variant="contained"
                >
                  {t('sign_in.sign_in')}
                </HighlightButton>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </LogInContainer>
  )
}

export default LogIn
